import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';

interface UseVoiceRecorderOptions {
  maxDuration?: number;
  sampleRate?: number;
  onComplete?: (blob: Blob) => void;
  onError?: (error: Error) => void;
}

interface UseVoiceRecorderReturn {
  isRecording: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  resetRecording: () => void;
  playRecording: () => void;
  pauseRecording: () => void;
  isPlaying: boolean;
  error: Error | null;
}

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): UseVoiceRecorderReturn {
  const {
    maxDuration = 3000,
    sampleRate = 16000,
    onComplete,
    onError
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // 清理函数
  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
    }

    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      // 检查浏览器支持
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        throw new Error('您的浏览器不支持录音功能');
      }

      // 请求麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      streamRef.current = stream;

      // 创建AudioContext用于音频分析
      audioContextRef.current = new AudioContext({ sampleRate });
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // 创建MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : 'audio/ogg';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        setIsRecording(false);
        onComplete?.(blob);
      };

      mediaRecorder.onerror = (e) => {
        console.error('MediaRecorder error:', e);
        setError(new Error('录音出错，请重试'));
        onError?.(new Error('录音出错'));
        stopRecording();
      };

      // 开始录音
      mediaRecorder.start(100); // 每100ms收集一次数据
      setIsRecording(true);
      setRecordingTime(0);
      setError(null);

      // 倒计时
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 0.1;
          if (newTime >= maxDuration / 1000) {
            stopRecording();
            return maxDuration / 1000;
          }
          return newTime;
        });
      }, 100);

    } catch (err) {
      const error = err instanceof Error ? err : new Error('录音启动失败');
      console.error('Start recording error:', error);
      setError(error);
      onError?.(error);
      
      // 显示友好的错误提示
      if (error.name === 'NotAllowedError') {
        toast.error('请允许使用麦克风权限');
      } else if (error.name === 'NotFoundError') {
        toast.error('未找到麦克风设备');
      } else {
        toast.error(error.message);
      }
    }
  }, [maxDuration, sampleRate, onComplete, onError, stopRecording]);

  const resetRecording = useCallback(() => {
    cleanup();
    audioElementRef.current = null;
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setError(null);
    setIsPlaying(false);
    chunksRef.current = [];
  }, [cleanup]);

  const playRecording = useCallback(() => {
    if (!audioUrl) return;

    if (!audioElementRef.current) {
      audioElementRef.current = new Audio(audioUrl);
      audioElementRef.current.onended = () => {
        setIsPlaying(false);
      };
      audioElementRef.current.onerror = () => {
        setIsPlaying(false);
        toast.error('播放失败');
      };
    } else if (audioElementRef.current.src !== audioUrl) {
      audioElementRef.current.src = audioUrl;
    }

    audioElementRef.current.play();
    setIsPlaying(true);
  }, [audioUrl]);

  useEffect(() => {
    audioElementRef.current = null;
  }, [audioUrl]);

  const pauseRecording = useCallback(() => {
    if (audioElementRef.current && isPlaying) {
      audioElementRef.current.pause();
      setIsPlaying(false);
    }
  }, [isPlaying]);

  return {
    isRecording,
    recordingTime,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    resetRecording,
    playRecording,
    pauseRecording,
    isPlaying,
    error
  };
}

// 获取音频波形数据（用于可视化）
export function useAudioVisualization() {
  const getWaveformData = useCallback((analyser: AnalyserNode): number[] => {
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);
    return Array.from(dataArray);
  }, []);

  return { getWaveformData };
}
